import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  Alert,
  Typography,
  Spin,
  Steps,
  Descriptions,
  Tag,
  message,
} from 'antd';
import ResponsiveModal from './ResponsiveModal';
import BundlePicker, { type BundleFamily } from './esim/BundlePicker';
import RoamingPicker from './esim/RoamingPicker';
import EsimGuideLink from './esim/EsimGuideLink';
import {
  provisionEsim,
  getEsimQrCode,
  getEsimBundles,
  getRoamingProfiles,
  type ProvisionEsimResult,
  type EsimBundle,
  type RoamingProfile,
} from '../services/adminService';
import { balanceTypeSummary, formatValidity, roamingLabel } from '../esim/bundleHelpers';

const { Text, Paragraph } = Typography;

interface ProvisionEsimModalProps {
  open: boolean;
  onClose: () => void;
  // Called after a successful submit so the parent can refresh the list.
  onProvisioned?: (iccid: string) => void;
}

// ApiClient throws `API Error: <status> <text> - <body>`; pull out the server's
// error/detail so the user sees a readable reason instead of the raw string.
export const readableError = (err: unknown): string => {
  const raw = err instanceof Error ? err.message : String(err);
  const bodyStart = raw.indexOf(' - ');
  if (bodyStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(bodyStart + 3));
      const parts = [parsed.error, parsed.detail].filter(Boolean);
      if (parts.length) return parts.join(' — ');
    } catch {
      // not JSON — fall through to the raw message
    }
  }
  return raw;
};

// KAN-167: guided eSIM provisioning in three steps — bundle, coverage, review.
// Everything the operator can choose comes from the mirrored Vodafone catalogue,
// so a typo can no longer reach the provider. Provisioning is asynchronous: the
// ICCID and QR arrive via the webhook and the eSIM appears in Inventory shortly.
const ProvisionEsimModal: React.FC<ProvisionEsimModalProps> = ({
  open,
  onClose,
  onProvisioned,
}) => {
  const [step, setStep] = useState(0);
  const [family, setFamily] = useState<BundleFamily>('travel-sim');
  const [bundleId, setBundleId] = useState<string | undefined>();
  const [bundle, setBundle] = useState<EsimBundle | undefined>();
  const [roaming, setRoaming] = useState<{ group: string; id: number } | null>(null);
  const [roamingProfile, setRoamingProfile] = useState<RoamingProfile | undefined>();
  const [customReference, setCustomReference] = useState('');

  const [bundles, setBundles] = useState<EsimBundle[]>([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [profiles, setProfiles] = useState<RoamingProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ProvisionEsimResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Load the catalogue and the coverage options when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBundlesLoading(true);
    setProfilesLoading(true);
    setLoadError(null);
    getEsimBundles()
      .then(res => {
        if (!cancelled) setBundles(res.bundles);
      })
      .catch(err => {
        if (!cancelled) setLoadError(readableError(err));
      })
      .finally(() => {
        if (!cancelled) setBundlesLoading(false);
      });
    getRoamingProfiles()
      .then(res => {
        if (!cancelled) setProfiles(res.profiles);
      })
      .catch(err => {
        if (!cancelled) setLoadError(readableError(err));
      })
      .finally(() => {
        if (!cancelled) setProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = () => {
    setStep(0);
    setFamily('travel-sim');
    setBundleId(undefined);
    setBundle(undefined);
    setRoaming(null);
    setRoamingProfile(undefined);
    setCustomReference('');
    setResult(null);
    setErrorMsg(null);
    setQrDataUrl(null);
    setSubmitting(false);
    setQrLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const fixedZone =
    bundle?.family === 'travel-zone' && bundle.defaultRoaming ? bundle.defaultRoaming : null;
  const effectiveRoaming = fixedZone || roaming;
  const canGoToCoverage = !!bundle;
  const canGoToReview = !!bundle && (!!fixedZone || !!roaming);

  const fetchQr = async (iccid: string) => {
    setQrLoading(true);
    try {
      const qr = await getEsimQrCode(iccid);
      setQrDataUrl(qr.dataUrl);
    } catch {
      message.warning(
        'Could not fetch the QR yet. It may not be ready — retry from the eSIM shortly.'
      );
    } finally {
      setQrLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!bundle) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await provisionEsim({
        bundleId: bundle.id,
        roamingControl: effectiveRoaming
          ? { id: effectiveRoaming.id, group: effectiveRoaming.group }
          : undefined,
        customReference: customReference.trim() || undefined,
      });
      setResult(res);
      message.success('eSIM provisioning submitted');
      onProvisioned?.(res.iccid || '');
      if (res.iccid) await fetchQr(res.iccid);
    } catch (err) {
      const detail = readableError(err);
      setErrorMsg(detail);
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const guideLink = <EsimGuideLink />;

  return (
    <ResponsiveModal
      title="Provision a Travel eSIM"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
    >
      {!result && (
        <>
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="This allocates a real eSIM from the Vodafone Travel pool"
            description={
              <>
                Pick the data bundle, then the coverage, then confirm. Vodafone allocates the
                profile within about a minute and the eSIM appears in Inventory with its activation
                QR code. The bundle's validity only starts when the eSIM first uses data.{' '}
                {guideLink}.
              </>
            }
          />
          {loadError && (
            <Alert
              style={{ marginBottom: 16 }}
              type="error"
              showIcon
              message="Could not load the catalogue"
              description={loadError}
            />
          )}
          {errorMsg && (
            <Alert
              style={{ marginBottom: 16 }}
              type="error"
              showIcon
              closable
              onClose={() => setErrorMsg(null)}
              message="Provisioning failed"
              description={errorMsg}
            />
          )}
          <Steps
            size="small"
            current={step}
            style={{ marginBottom: 20 }}
            items={[{ title: 'Bundle' }, { title: 'Coverage' }, { title: 'Review & provision' }]}
          />

          {step === 0 && (
            <BundlePicker
              bundles={bundles}
              loading={bundlesLoading}
              value={bundleId}
              family={family}
              onFamilyChange={f => {
                setFamily(f);
                setRoaming(null);
                setRoamingProfile(undefined);
              }}
              onChange={(id, b) => {
                setBundleId(id);
                setBundle(b);
                setRoaming(null);
                setRoamingProfile(undefined);
              }}
            />
          )}

          {step === 1 && bundle && (
            <RoamingPicker
              profiles={profiles}
              loading={profilesLoading}
              value={roaming}
              recommended={bundle.defaultRoaming}
              locked={
                fixedZone ? { ...fixedZone, name: bundle.name.replace(/\s+-\s+.*$/, '') } : null
              }
              onChange={(v, p) => {
                setRoaming(v);
                setRoamingProfile(p);
              }}
            />
          )}

          {step === 2 && bundle && (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Descriptions bordered size="small" column={1} title="What will be provisioned">
                <Descriptions.Item label="Bundle">
                  {bundle.name} — {bundle.data || '—'}, {formatValidity(bundle.validity)}
                </Descriptions.Item>
                <Descriptions.Item label="After the allowance">
                  <Tag color={bundle.balanceType.key === 'standard' ? 'blue' : 'orange'}>
                    {bundle.balanceType.label}
                  </Tag>{' '}
                  {balanceTypeSummary(bundle.balanceType)}
                </Descriptions.Item>
                <Descriptions.Item label="Coverage">
                  {fixedZone
                    ? `Fixed by the bundle (profile ${fixedZone.id}, ${fixedZone.group})`
                    : roamingProfile
                      ? `${roamingLabel(roamingProfile)} — ${roamingProfile.group}`
                      : roaming
                        ? `Profile ${roaming.id}, ${roaming.group}`
                        : '—'}
                </Descriptions.Item>
              </Descriptions>
              <Form layout="vertical" style={{ marginBottom: 0 }}>
                <Form.Item
                  label="Custom reference (optional)"
                  tooltip="Free text stored with the order at Vodafone, e.g. the vessel or customer name. Shown on the eSIM afterwards."
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    value={customReference}
                    onChange={e => setCustomReference(e.target.value)}
                    placeholder="e.g. MY Destiny — crew tablet"
                    maxLength={120}
                  />
                </Form.Item>
              </Form>
              <Alert
                type="warning"
                showIcon
                message="What happens after you click Provision"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>
                      Vodafone allocates a profile from the pool and applies the bundle and
                      coverage. This is asynchronous and usually takes under a minute.
                    </li>
                    <li>
                      The new eSIM appears in Inventory (sort by "Added"). Open it to show the
                      activation QR code to the end user.
                    </li>
                    <li>
                      The validity clock starts when the eSIM first uses data, not now. Remaining
                      data and expiry are shown on the eSIM page.
                    </li>
                    <li>
                      Data can be added later with "Top up" on the eSIM page (standard bundles only,
                      up to three stacked bundles).
                    </li>
                  </ul>
                }
              />
            </Space>
          )}

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Space>
              {step > 0 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
              {step === 0 && (
                <Button type="primary" disabled={!canGoToCoverage} onClick={() => setStep(1)}>
                  Next: coverage
                </Button>
              )}
              {step === 1 && (
                <Button type="primary" disabled={!canGoToReview} onClick={() => setStep(2)}>
                  Next: review
                </Button>
              )}
              {step === 2 && (
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={handleSubmit}
                  disabled={!canGoToReview}
                >
                  Provision eSIM
                </Button>
              )}
            </Space>
          </div>
        </>
      )}

      {result && (
        <div>
          <Alert
            style={{ marginBottom: 16 }}
            type="success"
            showIcon
            message="Provisioning submitted"
            description="Vodafone is allocating the eSIM. It arrives asynchronously and will appear in Inventory within about a minute — sort by the “Added” column to find it, then open it to view the activation QR and the bundle balance."
          />
          <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
            {result.bundle && (
              <Descriptions.Item label="Bundle">
                {result.bundle.name} — {result.bundle.data || '—'},{' '}
                {formatValidity(result.bundle.validity)}
              </Descriptions.Item>
            )}
            {result.roaming && (
              <Descriptions.Item label="Coverage">
                {result.roaming.name || `Profile ${result.roaming.id}`} ({result.roaming.group})
              </Descriptions.Item>
            )}
            {result.ackId && (
              <Descriptions.Item label="Reference">
                <Text copyable>{result.ackId}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
          {result.iccid && (
            <>
              <Paragraph strong>Activation QR code</Paragraph>
              {qrLoading ? (
                <Spin />
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`eSIM activation QR for ${result.iccid}`}
                  style={{ maxWidth: 240, width: '100%', border: '1px solid #eee', padding: 8 }}
                />
              ) : (
                <Text type="secondary">QR not available yet.</Text>
              )}
            </>
          )}
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
};

export default ProvisionEsimModal;
